import Icon from 'common-components/Icon'
import * as Styled from './styled'
import { Text } from 'common-components'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { useCallback, memo, useState, useEffect } from 'react'
import { useResponsive } from 'hooks/useResponsive'
import { PROJECT_MEDIA_TYPES, TABLET_SIZE_BREAKPOINT } from 'consts'
import {
  activeMediaItemAssetId,
  activeMediaItemImgPath,
  activeMediaItemType,
  projectDefaultVideoModel,
  selectActiveMedia
} from 'store/selectors'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { useGetDisabledState } from './useGetDisabledState'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { useCreditsCheck } from 'hooks/useGenerateVideo'
import ThunderIcon from 'assets/svgr-icons/Thunder.svg'
import MobileAnimatePopup from './MobilePopup'
import AnimateIcon from 'assets/svgr-icons/Animate.svg'
import AnimateGradientIcon from 'assets/svgr-icons/Animate_Gradient.svg'
import Tooltip from 'components/Tooltip'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'

const AnimateButton = () => {
  const hasVideoModels = useSelector(aiVideoSelectors.hasVideoModels)

  useEffect(() => {
    if (!hasVideoModels) {
      dispatch(aiVideoThunks.getAIVideoModels())
    }
  }, [hasVideoModels])

  const isTablet = useResponsive(TABLET_SIZE_BREAKPOINT)
  const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false)
  const activeMedia = useSelector(selectActiveMedia)
  const activeAssetId = useSelector(activeMediaItemAssetId)
  const activeMediaType = useSelector(activeMediaItemType)
  const activeMediaFilePath = useSelector(activeMediaItemImgPath)
  const { videoGenerationCost, modelCode } = useSelector(
    projectDefaultVideoModel
  )

  const isDisabled = useGetDisabledState()

  const { hasInsufficientCredits, handleInsufficientCredits } =
    useCreditsCheck(videoGenerationCost)

  const handleAnimate = useCallback(async () => {
    if (hasInsufficientCredits) {
      handleInsufficientCredits()
    } else {
      if (activeAssetId && activeMediaFilePath) {
        const isAiImage = activeMediaType === PROJECT_MEDIA_TYPES.aiImage
        dispatch(
          aiVideoThunks.instantImageAnimation({
            isAiImage,
            assetId: activeAssetId,
            mediaItemId: activeMedia?.id,
            filePath: activeMediaFilePath,
            modelCode,
            duration: activeMedia?.duration
          })
        )
      }
    }
  }, [
    activeAssetId,
    activeMediaFilePath,
    activeMedia,
    hasInsufficientCredits,
    handleInsufficientCredits,
    activeMediaType,
    modelCode
  ])

  const openMobilePopup = useCallback(() => {
    setIsMobilePopupOpen(true)
  }, [])

  const onCloseMobilePopup = useCallback(() => {
    setIsMobilePopupOpen(false)
  }, [])

  const webAnimateColor = hasInsufficientCredits ? 'white' : 'secondarySemiDark'

  return (
    <>
      <Tooltip tooltipText={isTablet ? translate(T.ANIMATE) : ''}>
        <Styled.AnimateButton
          onClick={isTablet ? openMobilePopup : handleAnimate}
          $disabled={isDisabled}
          $hasGradientBackground={hasInsufficientCredits}
        >
          {isTablet ? (
            <Icon
              icon={
                hasInsufficientCredits ? (
                  <AnimateGradientIcon />
                ) : (
                  <AnimateIcon />
                )
              }
              size="small"
              color="secondarySemiDark"
            />
          ) : (
            <>
              <Text type="h4" fontWeight="600" color={webAnimateColor}>
                {translate(T.ANIMATE)}
              </Text>
              <Styled.IconWithText>
                <Icon
                  icon={<ThunderIcon />}
                  size="small"
                  color={webAnimateColor}
                />
                <Text type="h4" color={webAnimateColor}>
                  {videoGenerationCost}
                </Text>
              </Styled.IconWithText>
            </>
          )}
        </Styled.AnimateButton>
      </Tooltip>

      <MobileAnimatePopup
        isOpen={isMobilePopupOpen}
        onClose={onCloseMobilePopup}
        videoGenerationCost={videoGenerationCost}
        hasInsufficientCredits={hasInsufficientCredits}
        handleAnimate={handleAnimate}
      />
    </>
  )
}

export default memo(AnimateButton)
